import React, { useMemo } from "react";
import { SuperPage, SuperPageAssignment, SuperPageServices } from "@/services/bedrock";
import { Combobox } from "@/components/ui";
import { publicWebOrigin, usePaginatedCombobox } from "@/utils";
import { SuperPageAssignmentStatus } from "./Assignments";

interface SuperPagePreviewProps {
  superPage: SuperPage;
  superPageAssignment?: SuperPageAssignment;
}

export const SuperPagePreview: React.FC<SuperPagePreviewProps> = ({ superPage, superPageAssignment }) => {
  const fetchAssignments = async (search: string | undefined, page: number) => {
    return await SuperPageServices.getSuperPageAssignments({
      superPageId: superPage.id,
      search,
      page,
      extraFields: ["player"],
    });
  };

  const assignmentsCombobox = usePaginatedCombobox<SuperPageAssignment>(fetchAssignments, false, superPageAssignment);

  const previewUrl = useMemo(() => {
    const params: { [key: string]: any } = {
      preview: true,
      backgroundColor: superPage.contentPageTheme?.backgroundColor,
      textColor: superPage.contentPageTheme?.textColor,
      primaryCtaColor: superPage.contentPageTheme?.primaryCtaColor,
      primaryCtaTextColor: superPage.contentPageTheme?.primaryCtaTextColor,
    };
    if (superPage.contentPageTheme?.headerMedia?.[0]?.uri) {
      params.headerUri = superPage.contentPageTheme?.headerMedia?.[0]?.uri;
    }
    if (assignmentsCombobox?.value?.player?.email) {
      params.e = assignmentsCombobox.value.player.email;
    }
    const queryString = new URLSearchParams(params).toString();
    return `${publicWebOrigin()}/v/${superPage.slug}?${queryString}`;
  }, [superPage.contentPageTheme, superPage.slug, assignmentsCombobox?.value?.player?.email]);

  const itemToStringLabel = (assignment: SuperPageAssignment) => {
    return assignment.player?.fullName ?? assignment.player?.email ?? "-";
  };

  const itemToIcon = (assignment: SuperPageAssignment) => {
    return <SuperPageAssignmentStatus status={assignment.status} viewedAt={assignment.viewedAt} includeTooltip />;
  };

  return (
    <div className="min-w-md w-7xl h-lvh flex flex-col gap-4">
      <Combobox
        {...assignmentsCombobox}
        itemToStringLabel={itemToStringLabel}
        itemToIcon={itemToIcon}
        placeholder="Select Player"
        showClear
        className="w-fit"
      />
      <iframe
        key={previewUrl}
        src={previewUrl}
        height={700}
        width={"100%"}
        seamless={true}
        className="no-scrollbar border-1 border-border rounded-lg shadow-md"
      />
    </div>
  );
};
